import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useNavigation } from '../../hooks/useNavigation';
import { useAuth } from '../../context/AuthContext';
import { useIsHealthOnlyAccess } from '../../hooks/useIsHealthOnlyAccess';
import DashboardQuickActionsSection from './DashboardQuickActionsSection';

function Icon({ name, className = '' }) {
  return (
    <span className={`material-symbols-rounded ${className}`.trim()} aria-hidden="true">
      {name}
    </span>
  );
}

/**
 * Non-admin dashboard home — mirrors prototype `#dashboardCommonHome`.
 */
function DashboardCommonHome() {
  const { t } = useTranslation();
  const { getTenantPath } = useNavigation();
  const { user } = useAuth();
  const isHealthOnlyAccess = useIsHealthOnlyAccess();

  // Prototype parity (CA 2a of PBI #3968): the Painel shows the full map of
  // areas for the Usuário Cliente profile. Rows are deliberately NOT filtered
  // by permission — a row the session cannot open is handled by the route's
  // own ProtectedRoute, which is the same behavior every other entry point in
  // the app has. Filtering here collapsed the page to two rows whenever the
  // session arrived without its permission list.
  const cards = [
    {
      id: 'agents',
      icon: 'smart_toy',
      title: t('dashboard.commonHome.cards.agents.title'),
      description: t('dashboard.commonHome.cards.agents.description'),
      links: [
        { label: t('dashboard.commonHome.cards.agents.agents'), path: 'ai-agents' },
        { label: t('dashboard.commonHome.cards.agents.projects'), path: 'ai-agents' },
        { label: t('dashboard.commonHome.cards.agents.conversations'), path: 'ai-agents' },
      ],
    },
    {
      id: 'workflows',
      icon: 'automation',
      title: t('dashboard.commonHome.cards.workflows.title'),
      description: t('dashboard.commonHome.cards.workflows.description'),
      links: [
        { label: t('navigation.automations'), path: 'automations' },
        { label: t('navigation.idp'), path: 'automation/idp' },
        { label: t('navigation.processMining'), path: 'bpmn' },
      ],
    },
    {
      id: 'dynamicService',
      icon: 'account_tree',
      title: t('dashboard.commonHome.cards.dynamicService.title'),
      description: t('dashboard.commonHome.cards.dynamicService.description'),
      links: [
        { label: t('navigation.campaigns'), path: 'campaigns' },
        { label: t('navigation.integrationsChannels'), path: 'integrations' },
        { label: t('navigation.hybridFlows'), path: 'automation/hybrid-flows' },
        { label: t('navigation.voiceMessaging'), path: 'voice-messaging' },
      ],
    },
    // Prototype parity (PBI #3968): the "Saúde" card's patientAgent/
    // accountability/examReading destinations still have no route, so those
    // three stay inert preview text. `service` (Atendimento) and `agenda`
    // (Agenda) now have real pages — link them.
    {
      id: 'health',
      icon: 'health_and_safety',
      title: t('dashboard.commonHome.cards.health.title'),
      description: t('dashboard.commonHome.cards.health.description'),
      links: [
        { label: t('dashboard.commonHome.cards.health.patientAgent'), path: 'health/whatsapp' },
        { label: t('dashboard.commonHome.cards.health.service'), path: 'health-care' },
        { label: t('dashboard.commonHome.cards.health.agenda'), path: 'health/agenda' },
        { label: t('dashboard.commonHome.cards.health.accountability') },
        { label: t('dashboard.commonHome.cards.health.examReading') },
      ],
    },
  ].filter((card) => !isHealthOnlyAccess || card.id === 'health');

  return (
    <div className="hub-dashboard-page section dashboard-common-home" data-testid="dashboard-common-home">
      {/* Same editable shortcuts the admin Home ships (prototype
        * `#dashboardCommonHome` carries the pencil too). The previous static
        * list here bypassed the editor and the per-profile access filter.
        * Skipped for health-only accounts: its catalog spans every section
        * (Executors, Automations, ...), not just Saúde. */}
      {!isHealthOnlyAccess ? <DashboardQuickActionsSection user={user} /> : null}

      <section className="dashboard-navigation-home" aria-labelledby="dashboardNavigationHomeTitle">
        <div className="dashboard-navigation-heading">
          <h2 id="dashboardNavigationHomeTitle">{t('dashboard.commonHome.title')}</h2>
          <p>{t('dashboard.commonHome.subtitle')}</p>
        </div>
        <div className={`dashboard-navigation-grid${cards.length === 1 ? ' dashboard-navigation-grid--single' : ''}`}>
          {cards.map((card) => (
            <article key={card.id} className="dashboard-navigation-card">
              <div className="dashboard-navigation-main">
                <Icon name={card.icon} className="dashboard-navigation-icon" />
                <span>
                  <strong>{card.title}</strong>
                  <small>{card.description}</small>
                </span>
              </div>
              <nav className="dashboard-navigation-submenu" aria-label={card.title}>
                {card.links.map((link) => (
                  link.path ? (
                    <Link key={`${card.id}-${link.path}-${link.label}`} to={getTenantPath(link.path)}>
                      {link.label}
                      <Icon name="chevron_right" />
                    </Link>
                  ) : (
                    <span
                      key={`${card.id}-preview-${link.label}`}
                      className="dashboard-navigation-preview"
                      aria-disabled="true"
                      title={t('dashboard.commonHome.comingSoon')}
                    >
                      {link.label}
                      <Icon name="chevron_right" />
                    </span>
                  )
                ))}
              </nav>
            </article>
          ))}
        </div>
      </section>
    </div>
  );
}

export default DashboardCommonHome;
